import React, { useEffect } from 'react';
import { CheckCircle, Globe, Shield } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About SendMyParcel India | International Courier Since 2003';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', "SendMyParcel India is an international courier and cargo company with 20+ years of experience, shipping from India to the USA, UK, Europe and beyond.");
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gray-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About SendMyParcel</h1>
          <p className="text-xl text-orange-400 font-medium">Connecting India to the World — Reliably, Rapidly, Right.</p>
          <p className="mt-6 text-gray-300 text-lg leading-relaxed">
            At SendMyParcel, we believe that every shipment tells a story — of a business expanding its reach, a family sending something precious, or an e-commerce brand building trust with a customer thousands of miles away. Since our founding in 2003, we have made it our mission to ensure that story always has a happy ending.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-orange-500 py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
          <div><p className="text-3xl font-bold">20+</p><p className="text-sm mt-1">Years of Experience</p></div>
          <div><p className="text-3xl font-bold">50+</p><p className="text-sm mt-1">Pickup Locations</p></div>
          <div><p className="text-3xl font-bold">190+</p><p className="text-sm mt-1">Countries Served</p></div>
          <div><p className="text-3xl font-bold">98%+</p><p className="text-sm mt-1">On-Time Delivery</p></div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-16">

        {/* Who We Are */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Who We Are</h2>
          <p className="text-gray-600 leading-relaxed">
            Priority Connections Pvt Ltd is a professionally managed, technology-driven courier and cargo company headquartered in New Delhi, with a strong operational presence across Mumbai, Bengaluru, Chennai, and Gurgaon. Incorporated as a Private Limited company in January 2007, we have spent over two decades building one of India's most dependable multichannel logistics networks.
          </p>
          <p className="text-gray-600 leading-relaxed mt-4">
            We are not just a courier company. We are your end-to-end logistics partner — from the moment your shipment is picked up at your doorstep in India to the moment it is delivered to a doorstep in New York, London, or Frankfurt.
          </p>
        </section>

        {/* What We Do */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What We Do</h2>
          <p className="text-gray-600 mb-6">We offer a full spectrum of domestic and international courier, cargo, and logistics services:</p>
          <ul className="space-y-3">
            {[
              ['International Courier & Cargo', 'Express and economy shipping to USA, UK, Europe, and 200+ countries worldwide'],
              ['Multichannel Shipping Solutions', 'Choose from air express, economy air, and road/sea options tailored to your timeline and budget'],
              ['E-Commerce Logistics', 'Scalable fulfilment and last-mile delivery solutions for online sellers and D2C brands shipping internationally'],
              ['Document & Parcel Delivery', 'Fast, secure delivery of legal documents, commercial papers, and personal parcels'],
              ['B2B Cargo Services', 'High-volume consignment management for corporate clients with custom SLA agreements'],
              ['Reverse Logistics', 'Seamless returns management to reduce asset recovery time and costs'],
              ['Warehousing', 'Pan-India warehousing infrastructure for storage and distribution'],
            ].map(([title, desc]) => (
              <li key={title} className="flex gap-3">
                <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600"><span className="font-semibold text-gray-800">{title}</span> — {desc}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Why Choose XFas */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Businesses Choose SendMyParcel</h2>
          <p className="text-gray-600 mb-6">
            With 50+ pickup locations across India and strategic alliances with globally recognised carrier networks, SendMyParcel offers the coverage of a large network with the personalised service of a dedicated partner.
          </p>
          <ul className="space-y-3">
            {[
              '20+ years of courier and logistics expertise',
              '50+ pickup locations across India',
              'Multi-carrier options — We are not tied to one carrier. We shop the best rate and transit time for your shipment',
              '98%+ on-time delivery service level target',
              'Real-time tracking with full shipment visibility',
              'Dedicated account managers for corporate and e-commerce clients',
              'Customs documentation support for all international shipments',
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Vision & Promise */}
        <section className="grid md:grid-cols-2 gap-8">
          <div className="bg-gray-50 rounded-2xl p-8">
            <Globe className="w-8 h-8 text-orange-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Our Vision</h2>
            <p className="text-gray-600 leading-relaxed">
              To be the most admired and trusted multichannel logistics brand in India — connecting Indian businesses and individuals to the world with speed, reliability, and transparency.
            </p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-8">
            <Shield className="w-8 h-8 text-orange-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Our Promise</h2>
            <p className="text-gray-600 leading-relaxed">
              We deliver on our commitments — to our clients, to their customers, and to our colleagues. Every shipment is handled with the same focus on accuracy, timeliness, and care that has defined SendMyParcel since day one.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-orange-500 rounded-2xl py-12 px-6 text-white">
          <h2 className="text-2xl font-bold mb-3">Ready to Ship with SendMyParcel?</h2>
          <p className="mb-6 text-orange-100">Get an instant quote and experience the SendMyParcel difference.</p>
          <a href="/#get-quote" className="inline-block bg-white text-orange-500 font-semibold px-8 py-3 rounded-lg hover:bg-orange-50 transition-colors">
            Get a Quote
          </a>
        </section>

      </div>
    </div>
  );
};

export default AboutPage;
